import type { Movimiento, ResumenCaja, AperturaCaja } from '@/modelos/cajas/Caja';

export interface ResumenPorMoneda {
  ingresos: number;
  egresos: number;
  cantidadMovimientos: number;
}

export const calcularResumenPorMoneda = (
  movimientos: Movimiento[],
  moneda: string
): ResumenPorMoneda => {
  const filtrados = movimientos.filter(m => (m.moneda ?? 'PEN') === moneda);
  return {
    ingresos: filtrados.filter(m => m.tipo === 'Ingreso').reduce((s, m) => s + m.monto, 0),
    egresos: filtrados.filter(m => m.tipo === 'Egreso').reduce((s, m) => s + m.monto, 0),
    cantidadMovimientos: filtrados.length,
  };
};

const EFECTIVO_SUNAT_CODE = '008';

const esMovimientoEfectivo = (m: Movimiento): boolean =>
  m.paymentMeanSunatCode === EFECTIVO_SUNAT_CODE ||
  (m.medioPago || '').toLowerCase().startsWith('efectivo') ||
  (m.fuente === 'movimiento' && !m.medioPago);

export const calcularResumenEfectivo = (
  movimientos: Movimiento[],
  moneda: string
): ResumenPorMoneda => {
  const filtrados = movimientos.filter(
    m => (m.moneda ?? 'PEN') === moneda && esMovimientoEfectivo(m)
  );
  return {
    ingresos: filtrados.filter(m => m.tipo === 'Ingreso').reduce((s, m) => s + m.monto, 0),
    egresos: filtrados.filter(m => m.tipo === 'Egreso').reduce((s, m) => s + m.monto, 0),
    cantidadMovimientos: filtrados.length,
  };
};

export const calcularResumenEfectivoTotal = (movimientos: Movimiento[]): ResumenPorMoneda => {
  const filtrados = movimientos.filter(esMovimientoEfectivo);
  return {
    ingresos: filtrados.filter(m => m.tipo === 'Ingreso').reduce((s, m) => s + m.monto, 0),
    egresos: filtrados.filter(m => m.tipo === 'Egreso').reduce((s, m) => s + m.monto, 0),
    cantidadMovimientos: filtrados.length,
  };
};

/**
 * Calcula el descuadre entre el monto ingresado y el saldo esperado
 */
export const calcularDescuadre = (montoIngresado: number, saldoEsperado: number): number => {
  return montoIngresado - saldoEsperado;
};

/**
 * Calcula el total de ingresos de una lista de movimientos
 */
export const calcularTotalIngresos = (movimientos: Movimiento[]): number => {
  return movimientos
    .filter(m => m.tipo === 'Ingreso')
    .reduce((sum, m) => sum + m.monto, 0);
};

/**
 * Calcula el total de egresos de una lista de movimientos
 */
export const calcularTotalEgresos = (movimientos: Movimiento[]): number => {
  return movimientos
    .filter(m => m.tipo === 'Egreso')
    .reduce((sum, m) => sum + m.monto, 0);
};

/**
 * Calcula el saldo neto (ingresos - egresos)
 */
export const calcularSaldoNeto = (ingresos: number, egresos: number): number => {
  return ingresos - egresos;
};

/**
 * Calcula el resumen completo de la caja
 */
export const calcularResumenCaja = (
  aperturaActual: AperturaCaja | null,
  movimientos: Movimiento[]
): ResumenCaja => {
  if (!aperturaActual) {
    return { apertura: 0, ingresos: 0, egresos: 0, saldo: 0, cantidadMovimientos: 0 };
  }

  const ingresos = calcularTotalIngresos(movimientos);
  const egresos = calcularTotalEgresos(movimientos);

  return {
    apertura: aperturaActual.montoInicialTotal,
    ingresos,
    egresos,
    saldo: aperturaActual.montoInicialTotal + ingresos - egresos,
    cantidadMovimientos: movimientos.length,
  };
};
